"use client"

import { cn } from "@/lib/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,

  FieldSeparator,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"

import { signInUser } from "@/server/users"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { useGoogleSignIn } from "./useGoogleSignIn"
import { LoaderCircle } from "lucide-react"


const formSchema = z.object({
  email: z.email(),
  password: z.string().min(8),
})

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"form">) {

  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    try {
      const response = await signInUser(values.email, values.password)
      if (response.success) {
        toast.success(response.message);
        router.push("/dashboard");

      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.log(error);

    } finally {
      setIsLoading(false);
    }
  }


  const { loading, signIn } = useGoogleSignIn();





  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={cn("flex flex-col gap-6", className)} {...props}>
        <div className="flex flex-col items-center   gap-1 mb-6">

          <h1 className="text-2xl text-center font-bold">Log in </h1>
          <FieldDescription >
            New to Habstrack?{" "}
            <Link href="/signup" className=" font-medium ">Create Account</Link>
          </FieldDescription>
        </div>


        <Field>
          <Button onClick={signIn} disabled={loading} variant="secondary" type="button" className="    ">
            {loading ? <LoaderCircle className=" animate-spin" /> : <>
              <svg width="294" height="300" viewBox="0 0 294 300" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M150 122.729V180.82H230.727C227.183 199.502 216.545 215.321 200.59 225.957L249.272 263.731C277.636 237.55 294 199.094 294 153.412C294 142.776 293.046 132.548 291.273 122.73L150 122.729Z" fill="#4285F4" />
                <path d="M65.9342 178.553L54.9546 186.958L16.0898 217.23C40.7719 266.185 91.3596 300.004 149.996 300.004C190.496 300.004 224.45 286.64 249.269 263.731L200.587 225.958C187.223 234.958 170.177 240.413 149.996 240.413C110.996 240.413 77.8602 214.095 65.9955 178.639L65.9342 178.553Z" fill="#34A853" />
                <path d="M16.0899 82.7734C5.86309 102.955 0 125.728 0 150.001C0 174.273 5.86309 197.047 16.0899 217.228C16.0899 217.363 66.0004 178.5 66.0004 178.5C63.0004 169.5 61.2272 159.955 61.2272 149.999C61.2272 140.043 63.0004 130.498 66.0004 121.498L16.0899 82.7734Z" fill="#FBBC05" />
                <path d="M149.999 59.7279C172.091 59.7279 191.727 67.3642 207.409 82.0918L250.364 39.1373C224.318 14.8647 190.5 0 149.999 0C91.3627 0 40.7719 33.6821 16.0898 82.7738L65.9988 121.502C77.8619 86.0462 110.999 59.7279 149.999 59.7279Z" fill="#EA4335" />
              </svg>
              Continue with Google
            </>}
          </Button>
        </Field>
        <FieldSeparator className="my-2">Or</FieldSeparator>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="email">Email</FormLabel>
              <FormControl>
                <Input className="     " id="email" type="email" placeholder="sam@gmail.com" required {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between ">
                <FormLabel htmlFor="password">Password</FormLabel>
                <FieldDescription>
                  <Link href="/forgot-password" className=" font-medium ">Forgot Password?</Link>
                </FieldDescription>
              </div>
              <FormControl>
                <Input id="password" type="password" className="     placeholder:font-extrabold pb-2 font-extrabold tracking-[4px]"
                  placeholder="••••••••" required {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Field>
          <Button disabled={isLoading} type="submit">
            {isLoading ? <LoaderCircle className=" animate-spin" /> : "Log in to your account"}
          </Button>
        </Field>
      </form>
    </Form >
  )
}
